import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import * as authApi from '../services/authApi'
import { resolvePostLoginRoute } from '@/lib/resolvePostLoginRoute'
import { setAuthToken } from '@/lib/authToken'

export function useAuth() {
  const navigate = useNavigate()

  const login = useCallback(
    async (payload: { email: string; password: string }) => {
      const result = await authApi.login(payload)
      setAuthToken(result.access_token)
      try {
        localStorage.setItem('user', JSON.stringify(result.user))
      } catch {}
      window.dispatchEvent(new Event('auth:login'))
      const { path, state } = await resolvePostLoginRoute()
      navigate(path, { replace: true, state })
    },
    [navigate],
  )

  // Exchanges the one-time code from /login/google/callback for a session.
  // New users go straight to onboarding, same as a fresh password signup;
  // everyone else follows the normal post-login routing.
  const loginWithGoogleCode = useCallback(
    async (code: string) => {
      const result = await authApi.exchangeGoogleSession(code)
      setAuthToken(result.access_token)
      try {
        localStorage.setItem('user', JSON.stringify(result.user))
      } catch {}
      window.dispatchEvent(new Event('auth:login'))
      if (result.isNewUser) {
        navigate('/onboarding', { replace: true })
        return
      }
      const { path, state } = await resolvePostLoginRoute()
      navigate(path, { replace: true, state })
    },
    [navigate],
  )

  // Exchanges the one-time code from /login/microsoft/callback for a session.
  // Mirrors loginWithGoogleCode above.
  const loginWithMicrosoftCode = useCallback(
    async (code: string) => {
      const result = await authApi.exchangeMicrosoftSession(code)
      setAuthToken(result.access_token)
      try {
        localStorage.setItem('user', JSON.stringify(result.user))
      } catch {}
      window.dispatchEvent(new Event('auth:login'))
      if (result.isNewUser) {
        navigate('/onboarding', { replace: true })
        return
      }
      const { path, state } = await resolvePostLoginRoute()
      navigate(path, { replace: true, state })
    },
    [navigate],
  )

  const register = useCallback(async (payload: Parameters<typeof authApi.register>[0]) => {
    const result = await authApi.register(payload)
    setAuthToken(result.access_token)
    try {
      localStorage.setItem('user', JSON.stringify(result.user))
    } catch {}
    return result
  }, [])

  const getMe = useCallback(async () => {
    return authApi.getMe()
  }, [])

  const resetPassword = useCallback(
    async (payload: { email: string; password: string; confirm_password: string }) => {
      return authApi.resetPassword(payload)
    },
    [],
  )

  return { login, loginWithGoogleCode, loginWithMicrosoftCode, register, getMe, resetPassword }
}
